import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Chip,
  Grid,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  BusinessCenter as BusinessCenterIcon,
  Assessment as AssessmentIcon,
  Analytics as AnalyticsIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

interface AISummaryTabProps {
  comprehensiveData: any[];
  dataFetchProgress?: {[key: string]: boolean};
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ai-summary-tabpanel-${index}`}
      aria-labelledby={`ai-summary-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `ai-summary-tab-${index}`,
    'aria-controls': `ai-summary-tabpanel-${index}`,
  };
}

const AISummaryTab: React.FC<AISummaryTabProps> = ({ comprehensiveData, dataFetchProgress = {} }) => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Helper function to render values properly
  const renderValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return '-';
    }
    
    if (typeof value === 'object') {
      // Handle objects with explanation, rating, confidence properties
      if (value.explanation || value.rating || value.confidence) {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {value.explanation && (
              <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                {value.explanation}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {value.rating && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
                    Risk Level:
                  </Typography>
                  <Chip 
                    label={value.rating} 
                    size="small"
                    sx={{
                      backgroundColor: value.rating === 'Low Risk' ? '#E8F5E9' :
                                     value.rating === 'Medium Risk' ? '#FFF3E0' : '#FBE9E7',
                      color: value.rating === 'Low Risk' ? '#2E7D32' :
                            value.rating === 'Medium Risk' ? '#F57C00' : '#D32F2F',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  />
                </Box>
              )}
              {value.confidence && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
                    Confidence:
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    p: 0.5,
                    px: 1,
                    backgroundColor: 'rgba(76, 55, 148, 0.1)',
                    borderRadius: 1,
                    color: '#4c3794',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}>
                    {typeof value.confidence === 'number' ? `${(value.confidence * 100).toFixed(0)}%` : value.confidence}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        );
      }
      
      // Handle arrays (like highlights, topics)
      if (Array.isArray(value)) {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {value.map((item, idx) => (
              <Chip
                key={idx}
                label={item}
                size="small"
                sx={{
                  backgroundColor: 'rgba(76, 55, 148, 0.1)',
                  color: '#4c3794',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: 'rgba(76, 55, 148, 0.15)',
                  }
                }}
              />
            ))}
          </Box>
        );
      }
      
      // Handle other objects by converting to string
      return (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
          {JSON.stringify(value, null, 2)}
        </Typography>
      );
    }
    
    // Handle primitive values
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    
    return '-';
  };

  // Credit Rating Report Renderer
  const renderCreditRatingReport = (entries: any, index: number) => (
    <Card key={index} sx={{ 
      mb: 4, 
      borderRadius: 3, 
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: '1px solid #e0e0e0'
    }}>
      {/* Header with gradient background */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #4c3794 0%, #3a2a73 100%)',
        p: 3,
        color: 'white'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          {entries.title || `Credit Rating Report ${index + 1}`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {entries.topics && Array.isArray(entries.topics) && entries.topics.length > 0 && 
            entries.topics.map((topic: string, idx: number) => (
              <Chip 
                key={idx}
                label={topic} 
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}
              />
            ))
          }
        </Box>
      </Box>

      <Box sx={{ p: 4 }}>
        {/* Description Sections */}
        {entries.briefDescription && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#4c3794' }} />
              Brief Description
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#333', p: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              {entries.briefDescription}
            </Typography>
          </Box>
        )}

        {entries.detailedDescription && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon sx={{ color: '#4c3794' }} />
              Detailed Analysis
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#333', p: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              {entries.detailedDescription}
            </Typography>
          </Box>
        )}

        

    

        {/* AI Risk Assessment */}
        {entries.contentRating && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AnalyticsIcon sx={{ color: '#4c3794' }} />
              AI Risk Assessment
            </Typography>
            <Box sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, border: '2px solid #e0e0e0' }}>
              {renderValue(entries.contentRating)}
            </Box>
          </Box>
        )}

        {/* Additional Details */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: 3,
          p: 3,
          backgroundColor: '#f8f9fa',
          borderRadius: 2
        }}>
          {entries.publishedBy && (
            <Box>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>
                Published By
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                {entries.publishedBy}
              </Typography>
            </Box>
          )}
          {entries.sourceName && (
            <Box>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>
                Source
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                {entries.sourceName}
              </Typography>
            </Box>
          )}
          {entries.contentClass && (
            <Box>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, display: 'block', mb: 0.5 }}>
                Content Type
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                {entries.contentClass}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Source Link */}
        {entries.sourceLink && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon sx={{ color: '#4c3794' }} />
              Source Link
            </Typography>
            <Box sx={{ p: 3, backgroundColor: 'rgba(76, 55, 148, 0.1)', borderRadius: 2, border: '2px solid rgba(76, 55, 148, 0.2)' }}>
              <a
                href={entries.sourceLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#4c3794', textDecoration: 'underline', fontSize: '0.95em', display: 'inline-flex', alignItems: 'center', gap: '0.25em', fontWeight: 500 }}
              >
                Preview
                <LinkIcon sx={{ fontSize: 16, ml: 0.5 }} />
              </a>
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );

  // Generic Dictionary Renderer (for unknown types) - Enhanced Design
  const renderGenericDictionary = (entries: any, index: number) => {
    // Extract key information for header
    const title = entries.title || entries.name || `Data Entry ${index + 1}`;
    const category = entries.contentClass || entries.category || 'General Data';
    const date = entries.date || entries.publishedDate || entries.createdDate;
    const source = entries.sourceName || entries.publishedBy || entries.agency;
    
    return (
      <Card key={index} sx={{ 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid #e0e0e0'
      }}>
        {/* Header with gradient background */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, #4c3794 0%, #3a2a73 100%)',
          p: 3,
          color: 'white'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {category && (
              <Chip 
                label={category} 
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 600
                }}
              />
            )}
            {source && (
              <Chip 
                label={source} 
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 600
                }}
              />
            )}
            {date && (
              <Chip 
                label={date} 
                size="small"
                sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 600
                }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          {/* Key Metrics Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {Object.entries(entries).slice(0, 4).map(([key, value], idx) => {
              if (['title', 'contentClass', 'category', 'date', 'sourceName', 'publishedBy', 'agency', 'highlights', 'uuid', 'orgCode', 'contentRelevance', 'publishedDate', 'creditRisks', 'campaignId', 'currentDate', 'informationCategory'].includes(key)) {
                return null;
              }
              
              return (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <Box sx={{ 
                    p: 3, 
                    backgroundColor: 'rgba(76, 55, 148, 0.1)',
                    borderRadius: 2,
                    textAlign: 'center',
                    border: '2px solid rgba(76, 55, 148, 0.2)'
                  }}>
                    <Typography variant="caption" sx={{ 
                      color: '#4c3794',
                      fontWeight: 600, 
                      display: 'block', 
                      mb: 1 
                    }}>
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      color: '#4c3794'
                    }}>
                      {typeof value === 'string' || typeof value === 'number' ? String(value) : 'View Details'}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {/* Brief Description */}
          {entries.briefDescription && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon sx={{ color: '#4c3794' }} />
                Brief Description
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#333', p: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                {entries.briefDescription}
              </Typography>
            </Box>
          )}

          {/* Detailed Analysis */}
          {entries.detailedDescription && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssessmentIcon sx={{ color: '#4c3794' }} />
                Detailed Analysis
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#333', p: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                {entries.detailedDescription}
              </Typography>
            </Box>
          )}

          {/* Topics */}
          {entries.topics && Array.isArray(entries.topics) && entries.topics.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessCenterIcon sx={{ color: '#4c3794' }} />
                Related Topics
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {entries.topics.map((topic: string, idx: number) => (
                  <Chip
                    key={idx}
                    label={topic}
                    size="medium"
                    sx={{
                      backgroundColor: '#E3F2FD',
                      color: '#1976D2',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#BBDEFB',
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* AI Risk Assessment */}
          {entries.contentRating && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
                <AnalyticsIcon sx={{ color: '#4c3794' }} />
                AI Risk Assessment
              </Typography>
              <Box sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, border: '2px solid #e0e0e0' }}>
                {renderValue(entries.contentRating)}
              </Box>
            </Box>
          )}

                  {/* Source Link */}
        {entries.sourceLink && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon sx={{ color: '#4c3794' }} />
              Source Document
            </Typography>
            <Box sx={{ p: 3, backgroundColor: '#E3F2FD', borderRadius: 2, border: '2px solid #BBDEFB' }}>
              <a
                href={entries.sourceLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#4c3794', textDecoration: 'underline', fontSize: '0.95em', display: 'inline-flex', alignItems: 'center', gap: '0.25em', fontWeight: 500 }}
              >
                Preview
                <LinkIcon sx={{ fontSize: 16, ml: 0.5 }} />
              </a>
            </Box>
          </Box>
        )}
        </Box>
      </Card>
    );
  };

  // Text Content Renderer - Enhanced Design
  const renderTextContent = (item: any, index: number) => {
    const title = item.title || `Text Content ${index + 1}`;
    const content = item.text || item.content || JSON.stringify(item);
    
    return (
      <Card key={index} sx={{ 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid #e0e0e0'
      }}>
        {/* Header with gradient background */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, #4c3794 0%, #3a2a73 100%)',
          p: 3,
          color: 'white'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label="Text Content" 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600
              }}
            />
            <Chip 
              label={`${content.length} characters`} 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600
              }}
            />
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#4c3794' }} />
              Content
            </Typography>
            <Typography variant="body1" sx={{ 
              lineHeight: 1.7, 
              color: '#333', 
              p: 3, 
              backgroundColor: 'rgba(76, 55, 148, 0.1)', 
              borderRadius: 2,
              border: '2px solid rgba(76, 55, 148, 0.2)'
            }}>
              {content}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  // Multi Text Options Renderer - Enhanced Design
  const renderMultiTextOptions = (item: any, index: number) => {
    const title = item.title || `Options ${index + 1}`;
    const options = item.options || item.value || [];
    
    return (
      <Card key={index} sx={{ 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid #e0e0e0'
      }}>
        {/* Header with gradient background */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, #4c3794 0%, #3a2a73 100%)',
          p: 3,
          color: 'white'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label="Multiple Options" 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600
              }}
            />
            <Chip 
              label={`${options.length} options`} 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600
              }}
            />
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessCenterIcon sx={{ color: '#4c3794' }} />
              Available Options
            </Typography>
            <Box sx={{ 
              p: 3, 
              backgroundColor: 'rgba(76, 55, 148, 0.1)', 
              borderRadius: 2,
              border: '2px solid rgba(76, 55, 148, 0.2)'
            }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {options.map((option: string, idx: number) => (
                  <Chip
                    key={idx}
                    label={option}
                    size="medium"
                    sx={{
                      backgroundColor: 'rgba(76, 55, 148, 0.15)',
                      color: '#4c3794',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      '&:hover': {
                        backgroundColor: '#4c3794',
                        color: 'white',
                        transform: 'scale(1.05)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  };

  // Default Content Renderer - Enhanced Design
  const renderDefaultContent = (item: any, index: number) => {
    const title = item.title || `Content ${index + 1}`;
    const contentType = item.objectType || 'Unknown Type';
    
    return (
      <Card key={index} sx={{ 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        border: '1px solid #e0e0e0'
      }}>
        {/* Header with gradient background */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, #4c3794 0%, #3a2a73 100%)',
          p: 3,
          color: 'white'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={contentType} 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600
              }}
            />
            <Chip 
              label="Raw Data" 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 600
              }}
            />
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon sx={{ color: '#4c3794' }} />
              Raw Data Content
            </Typography>
            <Box sx={{ 
              p: 3, 
              backgroundColor: 'rgba(76, 55, 148, 0.1)', 
              borderRadius: 2,
              border: '2px solid rgba(76, 55, 148, 0.2)'
            }}>
              <Typography variant="body2" sx={{ 
                fontFamily: 'monospace', 
                fontSize: '0.85rem',
                lineHeight: 1.6,
                color: '#4c3794',
                backgroundColor: 'rgba(255,255,255,0.7)',
                p: 2,
                borderRadius: 1,
                border: '1px solid rgba(76, 55, 148, 0.2)'
              }}>
                {JSON.stringify(item, null, 2)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  };

  // Function to determine content type and render accordingly
  const renderContentByType = (item: any, index: number) => {
    // If item has processedEntries, use those directly
    if (item.processedEntries) {
      const entries = item.processedEntries;
      
      // Use credit rating report renderer for all dictionary items
      if (item.objectType === 'DICTIONARY' || entries) {
        return renderCreditRatingReport(entries, index);
      }
    }
    
    // Handle original entries structure
    if (item.objectType === 'DICTIONARY' && item.entries && Array.isArray(item.entries)) {
      const entries = item.entries.reduce((acc: any, entry: any) => {
        acc[entry.key] = entry.value;
        return acc;
      }, {});

      // Use credit rating report renderer for all dictionary items
      return renderCreditRatingReport(entries, index);
    }
    
    // Handle other object types
    if (item.objectType === 'TEXT') {
      return renderTextContent(item, index);
    }
    
    if (item.objectType === 'MULTITEXTOPTIONS') {
      return renderMultiTextOptions(item, index);
    }
    
    // Default fallback
    return renderDefaultContent(item, index);
  };

  const otherData = comprehensiveData.find(d => d.category === 'OTHER');
  
  if (!otherData || !otherData.data || otherData.data.length === 0) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        mt: 8,
        p: 6,
        backgroundColor: '#f8f9fa',
        borderRadius: 3,
        border: '2px dashed #e0e0e0'
      }}>
        <InfoIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
        <Typography variant="h5" sx={{ color: '#666', mb: 2, fontWeight: 600 }}>
          No AI Summary Available
        </Typography>
        <Typography variant="body1" sx={{ color: '#999', maxWidth: 400, mx: 'auto' }}>
          AI-generated insights and analysis will appear here when data becomes available
        </Typography>
      </Box>
    );
  }

  // Handle the new data structure where data comes grouped by groupName
  const groupedData: { [key: string]: any[] } = {};
  
  otherData.data.forEach((group: any) => {
    if (group.groupName && group.items && Array.isArray(group.items)) {
      // Use groupName as the key
      const groupKey = group.groupName;
      
      if (!groupedData[groupKey]) {
        groupedData[groupKey] = [];
      }
      
      // Add all items from this group
      group.items.forEach((item: any) => {
        // Only include items where informationCategory is OTHER
        if (item.objectType === 'DICTIONARY' && item.entries && Array.isArray(item.entries)) {
          const entries = item.entries.reduce((entryAcc: any, entry: any) => {
            entryAcc[entry.key] = entry.value;
            return entryAcc;
          }, {});
          
          // Check if informationCategory is OTHER
          if (entries.informationCategory === 'OTHER') {
            groupedData[groupKey].push({ ...item, processedEntries: entries });
          }
        } else {
          // Handle non-dictionary items
          groupedData[groupKey].push(item);
        }
      });
    }
  });

  // Filter out empty groups
  const nonEmptyGroups = Object.entries(groupedData).filter(([_, items]) => items.length > 0);

  if (nonEmptyGroups.length === 0) {
    return (
      <Box sx={{ 
        textAlign: 'center', 
        mt: 8,
        p: 6,
        backgroundColor: '#f8f9fa',
        borderRadius: 3,
        border: '2px dashed #e0e0e0'
      }}>
        <InfoIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
        <Typography variant="h5" sx={{ color: '#666', mb: 2, fontWeight: 600 }}>
          No AI Summary Available
        </Typography>
        <Typography variant="body1" sx={{ color: '#999', maxWidth: 400, mx: 'auto' }}>
          No data with information category "OTHER" found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 280px)', overflowY: 'auto', p: 3 }}>
      <Box>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="AI Summary tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                minWidth: 'auto',
                px: 3,
                py: 2,
                color: '#666',
                '&.Mui-selected': {
                  color: '#4c3794',
                  fontWeight: 700,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#4c3794',
                height: 3,
              },
            }}
          >
            {nonEmptyGroups.map(([groupName, items], index) => (
              <Tab 
                key={groupName} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'inherit' }}>
                      {groupName}
                    </Typography>
                    <Chip 
                      label={items.length} 
                      size="small" 
                      sx={{ 
                        backgroundColor: tabValue === index ? '#4c3794' : '#e0e0e0',
                        color: tabValue === index ? 'white' : '#666',
                        fontSize: '0.75rem',
                        height: 20,
                        minWidth: 20,
                      }}
                    />
                  </Box>
                }
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Panels */}
        {nonEmptyGroups.map(([groupName, items], index) => (
          <TabPanel key={groupName} value={tabValue} index={index}>
            <Box>
              {items.map((item: any, itemIndex: number) => {
                if (item.processedEntries) {
                  // Use processed entries for dictionary items
                  return renderContentByType({ ...item, entries: item.processedEntries }, itemIndex);
                }
                return renderContentByType(item, itemIndex);
              })}
            </Box>
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export default AISummaryTab; 